import React from 'react';
import { node, string } from 'prop-types';
import classNames from 'classnames';
import loadable from '@loadable/component';
import IconSpinner from '../IconSpinner/IconSpinner';

import css from './FieldLoading.module.css';

/**
 * The stand-in a field shows while its code loads: its label and an empty field box of the same
 * size, with a spinner on the right. The field then takes its place without the form jumping.
 */
const FieldLoading = props => {
  const { rootClassName, className, id, label, placeholder } = props;
  return (
    <div className={classNames(rootClassName || css.root, className)} aria-busy="true">
      {label ? <label htmlFor={id}>{label}</label> : null}
      <div className={css.box}>
        <span className={css.placeholder}>{placeholder}</span>
        <IconSpinner rootClassName={css.spinner} />
      </div>
    </div>
  );
};

FieldLoading.defaultProps = {
  rootClassName: null,
  className: null,
  id: null,
  label: null,
  placeholder: null,
};

FieldLoading.propTypes = {
  rootClassName: string,
  className: string,
  id: string,
  label: node,
  placeholder: string,
};

/**
 * A field loaded in its own chunk (`loadable`) that shows FieldLoading, with its label and
 * placeholder, until the chunk is in. Keeps `preload` so a page can load it ahead of time.
 */
export const loadableField = (importComponent, options) => {
  const Field = loadable(importComponent, options);
  const LoadableField = props => (
    <Field
      fallback={
        <FieldLoading
          className={props.className}
          id={props.id}
          label={props.label}
          placeholder={props.placeholder}
        />
      }
      {...props}
    />
  );
  LoadableField.preload = Field.preload;
  LoadableField.load = Field.load;
  return LoadableField;
};

export default FieldLoading;
